import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import store from "../../../../state/store";
import PublicationLinks from "../../../../components/Publications/components/PublicationLinks";
import helpers from "../../../../common/utils/utils";
import InformationTable from "./InformationTableNew";
import { getVarsoUrl } from "../../../../common/helpers/helpers";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import {
  getDatabaseLink,
  sumFields,
  getMainName,
  countSubmissionsInAccessionArray,
  colorsForListElements,
  showVerdictLine,
} from "./helpers";
import { getNcbiClinvar2Data } from "../../../../state/selectors/NcbiClinvar2.selectors";
import { DATABASE_NAMES } from "./constants";
import NcbiClinvarDescriptions from "./NcbiClinvarDescriptions";
import { Modal } from "../../../../components/atoms";
import HelperText from "../../../../components/atoms/HelperText";

const getTitleDetails = (item) => {
  return (
    <div className="tw-inline-flex tw-items-center tw-float-right">
      <span className="tw-mr-3">
        <small>
          <a
            href={getVarsoUrl(
              `https://www.ncbi.nlm.nih.gov/clinvar/variation/${item.variation_id}`
            )}
            target="_blank"
          >
            View full records on ClinVar
          </a>
        </small>
      </span>
    </div>
  );
};

const getTitle = () => {
  return (
    <React.Fragment>
      <a
        href={getVarsoUrl("https://www.ncbi.nlm.nih.gov/clinvar/")}
        target="_blank"
      >
        ClinVar
      </a>
      <HelperText fieldDescriptionKey="ClinVar" />
    </React.Fragment>
  );
};

const NcbiClinvar2 = (props) => {
  const { ncbiClinvarData } = props;
  const [values, setValues] = useState([]);
  const [modalContent, setModalContent] = useState(null);

  useEffect(() => {
    setValues(ncbiClinvarData);
  }, [ncbiClinvarData]);

  const expandAccession = (acc) => {
    acc.expanded = !acc.expanded;
    setValues([...values]);
  };

  const getContent = (item, accessions, schema) => {
    return (
      <>
        <div>
          <InformationTable item={item} schema={schema} />
        </div>
        <div className="varsome-inner-container">{accessions}</div>
      </>
    );
  };

  const getAccessionRow = (
    i,
    actualDiseases,
    acc,
    origins,
    methods,
    symbols,
    diseasePubMedReferences
  ) => {
    return (
      <tr className="tw-border-t tw-border-blueGray-100" key={i}>
        <td>
          {" "}
          <small>
            <a
              onClick={() => {
                expandAccession(acc);
              }}
            >
              {acc.expanded ? "Hide " : "Show "}
              {acc.submissions.length} submission
              {acc.submissions.length > 1 ? "s" : ""}
            </a>
          </small>
        </td>
        <td>
          {actualDiseases && (
            <div>
              {actualDiseases.map((dis, idx) => {
                return (
                  <div key={idx}>
                    <NcbiClinvarDescriptions diseaseNames={dis.names} />
                    {!dis.names && (
                      <div>
                        <strong className="tw-pl-2">{acc.title}</strong>
                      </div>
                    )}
                    {dis.mode_of_inheritance && (
                      <div>
                        Mode of inheritance:
                        <strong className="tw-pl-2">
                          {dis.mode_of_inheritance}
                        </strong>
                      </div>
                    )}
                    {dis.age_of_onset && (
                      <div>
                        Age of onset:
                        <strong className="tw-pl-2">{dis.age_of_onset}</strong>
                      </div>
                    )}
                    {dis.prevalence && (
                      <div>
                        Prevalence:
                        <strong className="tw-pl-2">{dis.prevalence}</strong>
                      </div>
                    )}
                    {dis.keyword && (
                      <div>
                        Keyword:
                        <strong className="tw-pl-2">{dis.keyword}</strong>
                      </div>
                    )}
                    {dis.disease_mechanism && (
                      <div>
                        Disease mechanism:
                        <strong className="tw-pl-2">
                          {dis.disease_mechanism}
                        </strong>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </td>
        <td className="tw-text-center">
          <span className="tw-whitespace-nowrap">
            {helpers.parseNumericDate(acc.review_date)}
          </span>
          <br />
          {acc.review_status}
        </td>
        <td>{colorsForListElements(acc.clinical_significance)}</td>
        <td>{origins}</td>
        <td>{methods}</td>
        <td>
          {symbols}
          {diseasePubMedReferences.length > 0 && (
            <PublicationLinks ids={diseasePubMedReferences} />
          )}
        </td>
      </tr>
    );
  };

  const getSubmissions = (acc) => {
    let result = [];
    if (!acc.submissions) return result;
    if (acc.expanded) {
      for (let i = 0; i < acc.submissions.length; i++) {
        const subm = acc.submissions[i];
        const actualDiseases =
          subm.diseases || subm.finding || subm.drug_response;
        const symbols = [];
        let submPubMedReferences = [];

        if (subm.pub_med_references) {
          submPubMedReferences = subm.pub_med_references.slice();
        }

        if (actualDiseases) {
          for (let k = 0; k < actualDiseases.length; k++) {
            const dis = actualDiseases[k];
            if (dis.symbols) {
              let j = 0;
              for (const symbol in dis.symbols) {
                symbols.push(
                  <span key={`${String(j) + String(k)}dis`}>
                    {getDatabaseLink(symbol, dis.symbols[symbol])}
                    <HelperText fieldDescriptionKey={DATABASE_NAMES[symbol]} />
                    {(j < Object.keys(dis.symbols).length - 1 ||
                      dis.pub_med?.length > 0) &&
                      ", "}
                  </span>
                );
                j += 1;
              }
            }
          }
        }

        result.push(
          <tr>
            <td> </td>
            <td className="tw-text-zinc-700">
              {subm.submitter_name}
              {subm.submission_description.length > 0 ? (
                <div>
                  <input
                    type="button"
                    value="View ClinVar Comments"
                    onClick={() => {
                      setModalContent(subm.submission_description);
                    }}
                  />
                </div>
              ) : (
                ""
              )}
            </td>
            <td className="tw-text-zinc-700 tw-text-center">
              <span className="tw-whitespace-nowrap tw-text-zinc-700">
                {helpers.parseNumericDate(subm.review_date)}
              </span>
              <br />
              {subm.review_status}
            </td>
            <td>{colorsForListElements(subm.clinical_significance)}</td>
            <td className="tw-text-zinc-700">{subm.origin}</td>
            <td className="tw-text-zinc-700">{subm.method}</td>
            <td className="tw-text-zinc-700">
              {symbols}
              {submPubMedReferences.length > 0 && (
                <PublicationLinks ids={submPubMedReferences} />
              )}
            </td>
          </tr>
        );
      }
    } else {
      result = [];
    }
    return result;
  };

  let tmpl = null;

  if (store.getState().schema && ncbiClinvarData) {
    const schema = store.getState().schema.properties.ncbi_clinvar2;
    tmpl = (values || []).map((item, index) => {
      let accessions;
      const accessionsRows = [];
      if (item.accessions) {
        for (let i = 0; i < item.accessions.length; i++) {
          const acc = item.accessions[i];
          const diseasePubMedReferences = [];
          const actualDiseases =
            acc.diseases || acc.finding || acc.drug_response;
          const symbols = [];
          symbols.push(
            <div key={`accession_id${acc.accession_id}`}>
              <a
                target="_blank"
                href={getVarsoUrl(
                  `https://www.ncbi.nlm.nih.gov/clinvar/${acc.accession_id}`
                )}
              >
                {acc.accession_id}
              </a>
            </div>
          );

          if (actualDiseases) {
            for (let m = 0; m < actualDiseases.length; m++) {
              const dis = actualDiseases[m];
              if (dis.symbols) {
                let j = 0;
                for (const symbol in dis.symbols) {
                  const key = String(m) + String(j);
                  symbols.push(
                    <span key={key}>
                      {getDatabaseLink(symbol, dis.symbols[symbol])}
                      <HelperText
                        fieldDescriptionKey={DATABASE_NAMES[symbol]}
                      />
                      {(j < Object.keys(dis.symbols).length - 1 ||
                        dis.pub_med?.length > 0) &&
                        ", "}
                    </span>
                  );
                  j += 1;
                }
              }

              if (dis.pub_med) {
                for (let u = 0; u < dis.pub_med.length; u++) {
                  diseasePubMedReferences.push(dis.pub_med[u]);
                }
              }
            }
          }

          const origins = sumFields(acc, "origin");
          const methods = sumFields(acc, "method");

          const row = getAccessionRow(
            i,
            actualDiseases,
            acc,
            origins,
            methods,
            symbols,
            diseasePubMedReferences
          );
          accessionsRows.push(row);
          const submissions = getSubmissions(acc, i);
          for (let r = 0; r < submissions.length; r++) {
            accessionsRows.push(submissions[r]);
          }
        }

        if (accessionsRows) {
          accessions = (
            <div>
              <h4 className="tw-m-0">
                {item.accessions.length > 1 ? (
                  <span>{item.accessions.length} Accessions, </span>
                ) : (
                  <span>{item.accessions.length} Accession, </span>
                )}
                {countSubmissionsInAccessionArray(item.accessions) > 1 ? (
                  <span>
                    {" "}
                    {countSubmissionsInAccessionArray(item.accessions)}{" "}
                    Submissions
                  </span>
                ) : (
                  <span>
                    {" "}
                    {countSubmissionsInAccessionArray(item.accessions)}{" "}
                    Submission
                  </span>
                )}
              </h4>
              <table className="varsome-table">
                <thead>
                  <tr>
                    <th width="10%"> </th>
                    <th width="20%">Description/Submitter</th>
                    <th width="20%" className="tw-text-center">
                      Review status
                    </th>
                    <th width="10%">
                      Clinical significance {showVerdictLine(item.accessions)}
                    </th>
                    <th width="10%">Origin</th>
                    <th width="10%">Method</th>
                    <th width="20%">References</th>
                  </tr>
                </thead>
                <tbody className="tw-border-b tw-border-blueGray-100">
                  {accessionsRows}
                </tbody>
              </table>
            </div>
          );
        }
      }

      return (
        <div key={index} className="varsome-result-fixed">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              title={getTitle()}
              version={item.version}
              informativeMessage={<small>{getMainName(item.names)}</small>}
              titleDetailsRight={getTitleDetails(item)}
              content={getContent(item, accessions, schema)}
            />
          </div>
          <Modal
            isOpen={Boolean(modalContent)}
            className="tw-max-w-screen-xl"
            onRequestClose={() => {
              setModalContent(null);
            }}
            ContentRender={() => (
              <span>
                <div className="tw-text-center tw-font-bold tw-text-xl tw-py-3">
                  ClinVar Comments
                </div>
                <div className="tw-px-8 tw-text-justify tw-pb-8">
                  {modalContent}
                </div>
              </span>
            )}
          />
        </div>
      );
    });
  }

  return tmpl;
};

NcbiClinvar2.propTypes = {
  ncbiClinvarData: PropTypes.arrayOf(PropTypes.shape()),
};

NcbiClinvar2.defaultProps = {
  ncbiClinvarData: null,
};

const mapStateToProps = (state) => ({
  ncbiClinvarData: getNcbiClinvar2Data(state),
});

export default connect(
  mapStateToProps,
  {}
)(withErrorGuard(NcbiClinvar2, "NcbiClinvar2", "ClinVar"));
